import {
  type VisibleRangeProfileDisplaySettings,
  type VisibleRangeProfileKind,
} from '@/features/chart/lib/visible-range-profile-studies'
import type {
  TVVisiblePriceRange,
  TVVisibleTimeRange,
} from '@/lib/tradingview/types/tv-widget.types'
import type { VisibleRangeProfileUnit } from '@/types/dashboard'
import { Exchange, type Instrument, InstrumentKind } from '@/types/generated/common/types'
import type { VisibleRangeProfile, VisibleRangeProfileRequest } from '@/types/generated/public_api'

export const VISIBLE_RANGE_PROFILE_BUCKETS = 48
export const MAX_VISIBLE_RANGE_PROFILE_BUCKETS = 500

/**
 * Open interest is only meaningful for perpetuals/dated futures — spot comes
 * back all-zero and options are rejected with 400 by the backend, so the
 * indicator shouldn't be offered/enabled outside INSTRUMENT_KIND_FUTURE.
 */
export function isOpenInterestProfileSupported(instrument: Instrument | null | undefined) {
  return instrument?.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE
}

export type VisibleRangeProfileBucket = {
  index: number
  low: number
  high: number
  mid: number
  /** The "up" side of the bucket: buy volume, buy liquidations, or OI increase. */
  positive: number
  /** The "down" side of the bucket: sell volume, sell liquidations, or OI decrease. */
  negative: number
  isValueArea?: boolean
}

export function resolveVisibleRangeProfileBucketCount({
  settings,
  priceRange,
  tickSize,
}: {
  settings: VisibleRangeProfileDisplaySettings
  priceRange: TVVisiblePriceRange | null
  tickSize?: number
}): number {
  const rowSize = Math.max(1, Math.floor(settings.rowSize))
  if (settings.rowsLayout === 'numberOfRows' || !priceRange || !tickSize || tickSize <= 0) {
    return Math.min(rowSize, MAX_VISIBLE_RANGE_PROFILE_BUCKETS)
  }

  const span = Math.abs(priceRange.to - priceRange.from)
  const buckets = Math.ceil(span / (tickSize * rowSize))
  return Math.max(1, Math.min(buckets, MAX_VISIBLE_RANGE_PROFILE_BUCKETS))
}

type NumericArrayField = {
  [K in keyof VisibleRangeProfile]-?: VisibleRangeProfile[K] extends readonly number[] ? K : never
}[keyof VisibleRangeProfile]

const PROFILE_FIELD_MAP: Record<
  VisibleRangeProfileKind,
  Record<VisibleRangeProfileUnit, { positive: NumericArrayField; negative: NumericArrayField }>
> = {
  volume: {
    base: { positive: 'buyVolumeBase', negative: 'sellVolumeBase' },
    quote: { positive: 'buyVolumeQuote', negative: 'sellVolumeQuote' },
  },
  liquidation: {
    base: { positive: 'buyLiquidationBase', negative: 'sellLiquidationBase' },
    quote: { positive: 'buyLiquidationQuote', negative: 'sellLiquidationQuote' },
  },
  openInterest: {
    base: { positive: 'openInterestIncreaseBase', negative: 'openInterestDecreaseBase' },
    quote: { positive: 'openInterestIncreaseQuote', negative: 'openInterestDecreaseQuote' },
  },
}

export function buildVisibleRangeProfileRequest({
  exchange,
  symbol,
  timeRange,
  priceRange,
  buckets = VISIBLE_RANGE_PROFILE_BUCKETS,
}: {
  exchange: Exchange
  symbol: string
  timeRange: TVVisibleTimeRange
  priceRange: TVVisiblePriceRange | null
  buckets?: number
}): VisibleRangeProfileRequest | null {
  const fromMs = Math.floor(timeRange.from * 1000)
  const toMs = Math.ceil(timeRange.to * 1000)
  if (!symbol || !Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
    return null
  }

  const request: VisibleRangeProfileRequest = {
    exchange,
    symbol,
    fromMs: String(fromMs),
    toMs: String(toMs),
    buckets: Math.max(1, Math.min(Math.floor(buckets), MAX_VISIBLE_RANGE_PROFILE_BUCKETS)),
  }

  if (priceRange) {
    const priceMin = Math.min(priceRange.from, priceRange.to)
    const priceMax = Math.max(priceRange.from, priceRange.to)
    if (
      Number.isFinite(priceMin) &&
      Number.isFinite(priceMax) &&
      priceMin > 0 &&
      priceMax > priceMin
    ) {
      request.priceMin = priceMin
      request.priceMax = priceMax
    }
  }

  return request
}

export function selectProfileBuckets(
  profile: VisibleRangeProfile,
  kind: VisibleRangeProfileKind,
  unit: VisibleRangeProfileUnit,
  settings?: VisibleRangeProfileDisplaySettings,
  tickSize?: number
): VisibleRangeProfileBucket[] {
  const fields = PROFILE_FIELD_MAP[kind][unit]
  const positive = profile[fields.positive] as readonly number[]
  const negative = profile[fields.negative] as readonly number[]

  const length = Math.min(positive.length, negative.length)
  const source = Array.from({ length }, (_, index) => {
    const low = profile.priceMin + index * profile.bucketWidth
    const high = profile.bucketWidth === 0 ? low : low + profile.bucketWidth
    return {
      index,
      low,
      high,
      mid: profile.bucketWidth === 0 ? low : low + profile.bucketWidth / 2,
      positive: Number.isFinite(positive[index]) ? positive[index] : 0,
      negative: Number.isFinite(negative[index]) ? negative[index] : 0,
    }
  })

  const requestedCount = settings
    ? resolveVisibleRangeProfileBucketCount({
        settings,
        priceRange: {
          from: profile.priceMin,
          to: profile.priceMin + profile.bucketWidth * source.length,
        },
        tickSize,
      })
    : source.length
  const targetCount = Math.min(requestedCount, source.length)
  const buckets =
    targetCount === source.length
      ? source
      : Array.from({ length: targetCount }, (_, targetIndex) => {
          const start = Math.floor((targetIndex * source.length) / targetCount)
          const end = Math.max(
            start + 1,
            Math.floor(((targetIndex + 1) * source.length) / targetCount)
          )
          const members = source.slice(start, end)
          return {
            index: targetIndex,
            low: members[0].low,
            high: members.at(-1)?.high ?? members[0].high,
            mid: (members[0].low + (members.at(-1)?.high ?? members[0].high)) / 2,
            positive: members.reduce((sum, bucket) => sum + bucket.positive, 0),
            negative: members.reduce((sum, bucket) => sum + bucket.negative, 0),
          }
        })

  const volumeMode = settings?.volumeMode ?? 'upDown'
  const displayed = buckets.map((bucket) => {
    if (volumeMode === 'total') {
      return { ...bucket, positive: bucket.positive + bucket.negative, negative: 0 }
    }
    if (volumeMode === 'delta') {
      const delta = bucket.positive - bucket.negative
      return {
        ...bucket,
        positive: Math.max(delta, 0),
        negative: Math.max(-delta, 0),
      }
    }
    return bucket
  })

  if (!settings) return displayed

  const total = displayed.reduce(
    (sum, bucket) => sum + Math.abs(bucket.positive) + Math.abs(bucket.negative),
    0
  )
  if (total <= 0) return displayed

  const target = total * Math.max(0, Math.min(settings.valueAreaPercent, 100)) * 0.01
  if (target <= 0) return displayed
  let pointOfControl = 0
  let pointOfControlVolume = -Infinity
  displayed.forEach((bucket, index) => {
    const volume = Math.abs(bucket.positive) + Math.abs(bucket.negative)
    if (volume > pointOfControlVolume) {
      pointOfControl = index
      pointOfControlVolume = volume
    }
  })

  const included = new Set<number>([pointOfControl])
  let includedVolume = pointOfControlVolume
  let lower = pointOfControl - 1
  let upper = pointOfControl + 1
  while (includedVolume < target && (lower >= 0 || upper < displayed.length)) {
    const lowerVolume =
      lower >= 0 ? Math.abs(displayed[lower].positive) + Math.abs(displayed[lower].negative) : -1
    const upperVolume =
      upper < displayed.length
        ? Math.abs(displayed[upper].positive) + Math.abs(displayed[upper].negative)
        : -1
    const nextIndex = upperVolume > lowerVolume ? upper++ : lower--
    included.add(nextIndex)
    includedVolume += Math.max(lowerVolume, upperVolume)
  }

  return displayed.map((bucket, index) => ({ ...bucket, isValueArea: included.has(index) }))
}
